import { getUserInfo } from '../services/services';
import { isCompleteProfile } from '../redux/reducers/userInformation';

// Backoff between profile-fetch attempts. One entry per retry.
export const PROFILE_RETRY_DELAYS = [1000, 3000, 6000];

// The retry chain outlives the session it started in: a sign-out and sign-in as
// a different user can land inside the backoff window. Tier B resolves the
// profile from the `userName` query param, so an in-flight attempt would return
// the PREVIOUS user's profile with a 200 and overwrite the new one. Distinct
// from a plain failure so a superseded chain doesn't raise the banner either.
export const PROFILE_SUPERSEDED = 'superseded';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Fetches the user profile, retrying anything that isn't a 401.
 *
 * Before this existed a failed fetch was terminal: nothing was dispatched, the
 * caller's effect dependencies never changed, and the session sat
 * half-permissioned until some unrelated screen happened to refetch.
 *
 * Why retrying is the right shape here: prod CloudWatch shows Tier B answering
 * `get-user` correctly and quickly all the way through the reported outage —
 * the profile was found and returned every time. So what reaches the browser,
 * not what the server computed, is what goes wrong, and a second attempt is
 * likely to land. Retrying costs one extra call; not retrying cost a customer
 * an hour.
 *
 * @returns true when a usable profile was handed to onProfile, PROFILE_SUPERSEDED
 *   when the session changed mid-flight, false when the retries ran out or a 401
 *   ended the session.
 */
export async function fetchProfileWithRetry({
  cognitoUsername,
  retries = 0,
  onProfile,
  onUnauthorized,
  delays = PROFILE_RETRY_DELAYS,
  fetchUser = getUserInfo,
  wait = sleep
}) {
  if (!cognitoUsername) return false;

  // Identity this chain belongs to. The token is the reliable marker: sign-in,
  // sign-up and sign-out all rewrite or clear it, so a change means we are no
  // longer the current session and must not touch the store.
  const tokenAtStart = sessionStorage.getItem('token');
  const isSameSession = () => {
    if (sessionStorage.getItem('token') !== tokenAtStart) return false;
    // cognitoUsername is written by signIn.jsx but NOT by signUp.jsx, and the
    // caller may hold it from the redux slice instead. Absent means "no
    // opinion" — treating it as a mismatch would abandon every sign-up session
    // and the socket-driven refresh along with it.
    const storedUsername = sessionStorage.getItem('cognitoUsername');
    return !storedUsername || storedUsername === cognitoUsername;
  };

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    const result = await fetchUser(cognitoUsername);
    if (!isSameSession()) return PROFILE_SUPERSEDED;

    const profile = result?.data?.payload?.data;
    // A 200 carrying a profile without `user` is a failure too — the reducer
    // rejects it, so treating it as success would hide the problem.
    //
    // So is a 200 carrying `user` but no `role`. That one is what reached Frank
    // M. Booth on 2026-08-13: it satisfied the old `isUsableProfile` check, so
    // the chain reported success, stopped retrying and raised nothing, while
    // every `rolePermissions?.[role]` gate in the app read undefined. Accepting
    // it is precisely how a degraded session goes silent.
    if (result?.status === 200 && isCompleteProfile(profile)) {
      onProfile(profile);
      return true;
    }
    if (result?.response?.status === 401) {
      onUnauthorized();
      return false;
    }
    if (attempt < retries) {
      await wait(delays[attempt]);
    }
  }
  return false;
}
